import { Typography } from '@workspace/ui/components/Typography';
import { cn } from '@workspace/ui/lib/utils';
import { company } from '~/company/Company';
import { useGlobal } from '~/hooks/useGlobal';

interface CategoryBlockTitleProps {
  children: React.ReactNode;
  /** Semantic element to render the title as */
  as?: React.ElementType;
  /** Use inverse color when the block sits on a branded background */
  inverse?: boolean;
  className?: string;
}

/**
 * Single source of truth for the category block title style.
 *
 * Encodes the unified typography variant and color across every category
 * block, including the mcosmetic (semibold + brand), heimkaup (brand) and
 * cerise (DM Sans bold, smaller heading) overrides. Layout, "view all"
 * controls and navigation stay the responsibility of each consuming block.
 */
export const CategoryBlockTitle: React.FC<CategoryBlockTitleProps> = ({
  children,
  as = 'h2',
  inverse = false,
  className,
}) => {
  const { isMobile } = useGlobal();

  const TitleComponent = company.match({
    mcosmetic: isMobile
      ? Typography.Header150Semibold
      : Typography.Header400Semibold,
    cerise: isMobile
      ? Typography.Header100Semibold
      : Typography.Header150Semibold,
    default: isMobile ? Typography.Header100Medium : Typography.Header400Medium,
  });

  const colorClassName = inverse
    ? 'text-text-primary-inverse!'
    : company.match({
        mcosmetic: 'text-graphic-regular-brand!',
        heimkaup: 'text-graphic-regular-brand!',
        default: 'text-text-primary!',
      });

  // Cerise renders the heading in DM Sans Bold (700); DM Sans Black (900) is
  // not bundled, so the chosen weight is the heaviest real cut available.
  const weightClassName = company.match({ cerise: 'font-bold!' });

  return (
    <TitleComponent
      as={as}
      className={cn(colorClassName, weightClassName, className)}
    >
      {children}
    </TitleComponent>
  );
};
